import usePlacesAutocomplete from "use-places-autocomplete";
import { InputGroupInput } from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, type CSSProperties } from "react";

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: unknown;
      };
    };
  }
}

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  autoFocus?: boolean;
  /** Render suggestions with fixed positioning so overflow-clipping ancestors (e.g. tables) don't cut them off. */
  floatingDropdown?: boolean;
}

// Inner component that uses the hook - only rendered after script is loaded
function PlacesAutocompleteInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  className,
  disabled,
  id,
  autoFocus,
  floatingDropdown
}: PlacesAutocompleteProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
    defaultValue: value,
    cache: 86400,
  });

  // The input is disabled until the Places library is ready, so a mount-time
  // autoFocus lands on a disabled field; focus once it can take it.
  useEffect(() => {
    if (autoFocus && ready) inputRef.current?.focus();
  }, [autoFocus, ready]);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setValue(value, false);
    }
  }, [value, inputValue, setValue]);

  const handleSelect = async (address: string) => {
    setValue(address, false);
    onChange(address);
    clearSuggestions();
    
    // Trigger onBlur to validate the field after selection
    if (onBlur) {
      onBlur();
    }
  };

  const rect = floatingDropdown ? wrapperRef.current?.getBoundingClientRect() : undefined;

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <InputGroupInput
        ref={inputRef}
        id={id}
        value={inputValue}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={() => {
          // Allow suggestions to be clicked before hiding them
          setTimeout(() => {
            clearSuggestions();
            if (onBlur) onBlur();
          }, 200);
        }}
        onKeyDown={onKeyDown}
        disabled={!ready || disabled}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {status === "OK" && data.length > 0 && (
        <div
          className={cn(
            "z-50 mt-1 overflow-hidden rounded-md border border-input bg-popover shadow-md",
            floatingDropdown ? "fixed" : "absolute top-full left-0 right-0",
            rect && "top-(--places-dropdown-top) left-(--places-dropdown-left) w-(--places-dropdown-width)"
          )}
          style={
            rect
              ? ({
                  "--places-dropdown-top": `${rect.bottom}px`,
                  "--places-dropdown-left": `${rect.left}px`,
                  "--places-dropdown-width": `${Math.max(rect.width, 240)}px`,
                } as CSSProperties)
              : undefined
          }
        >
          <ul className="py-1 max-h-[300px] overflow-y-auto">
            {data.map(({ place_id, description }) => (
              <li 
                key={place_id}
                className="cursor-pointer px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                onClick={() => handleSelect(description)}
              >
                {description}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function PlacesAutocomplete({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = "Start typing your address...",
  className = "",
  disabled = false,
  id,
  autoFocus,
  floatingDropdown
}: PlacesAutocompleteProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_AUTOFILL_KEY;
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Load Google Maps script on first use
  useEffect(() => {
    if (!apiKey) return;

    // Check if already loaded
    if (window.google?.maps?.places) {
      setMapsLoaded(true);
      return;
    }

    // Check if script is already being loaded by another component
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]') as HTMLScriptElement | null;
    if (existingScript) {
      // Script exists, wait for it to load
      const checkLoaded = () => {
        if (window.google?.maps?.places) {
          setMapsLoaded(true);
        }
      };

      existingScript.addEventListener('load', () => {
        setTimeout(checkLoaded, 100);
      });

      // Also poll in case it's already loaded
      const pollInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          setMapsLoaded(true);
          clearInterval(pollInterval);
        }
      }, 100);

      return () => clearInterval(pollInterval);
    }

    // Load the script for the first time
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => setMapsLoaded(true), 100);
    };
    document.head.appendChild(script);
  }, [apiKey]);

  if (!apiKey) {
    return (
      <InputGroupInput
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        autoFocus={autoFocus}
      />
    );
  }

  // Only render the hook component after script is loaded
  if (!mapsLoaded) {
    return (
      <InputGroupInput
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        disabled={true}
        autoFocus={autoFocus}
      />
    );
  }

  return (
    <PlacesAutocompleteInput
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      id={id}
      autoFocus={autoFocus}
      floatingDropdown={floatingDropdown}
    />
  );
}
